import { useNavigate } from "react-router-dom"
import { getStudentSubmissionId } from "helpers/getSubmissionId"
import { StudentAssignmentResponse } from "shared/types/studentTypes"
import { StudentReviewAssignment } from "shared/types/science35Types"
import assessments from "shared/routes/assessments/assessments"
import lessons from "shared/lessons"
import { StudentAssignmentStateProps, Type } from "shared/types/moduleTypes"
import { toast } from "react-toastify"
import styles from "./styles.module.scss"
import { isMastered } from "shared/helpers/calculateMastery"

const { stepSuccess, stepFailure, stepNotStarted } = styles

const gradeStatus = (grade: number) => {
    if (grade == null) return "Not Started"
    else if (grade >= 75) return "Success"
    else return "Failure"
}

const StudentCoreIdeaDetails = ({ reviewAssignment } : { reviewAssignment: StudentReviewAssignment }) => {
    const navigate = useNavigate()

    const openAssignment = async (assignment: StudentAssignmentResponse, reviewPart: "pretest" | "lesson", knowledgeCheckAssessmentId?: string) => {
        try {
            // If there is a submission with a grade, then we should start the review lesson
            if (reviewPart === "pretest") {
                const pretestAssessmentId = (assignment.useGrade3 && assignment.review?.grade3pretestId) ? assignment.review?.grade3pretestId : assignment.review?.pretestId
                const pretestAssessment = await assessments.findById({ id: pretestAssessmentId })

                assignment.moduleName = pretestAssessment?.module
                assignment.assessmentNumber = pretestAssessment?.number
                assignment.type = pretestAssessment?.type as Type
                assignment.assessmentId = pretestAssessment?.id
            } else {
                const lessonId = (assignment.useGrade3 && assignment.review?.grade3lessonId) ? assignment.review?.grade3lessonId : assignment.review?.lessonId
                const lesson = await lessons.findById(lessonId)
                const postTestAssessmentId = (assignment.useGrade3 && assignment.review?.grade3postTestId) ? assignment.review?.grade3postTestId : (knowledgeCheckAssessmentId ?? lesson?.assessment?.id)
                const postTestAssessment = await assessments.findById({ id: postTestAssessmentId })

                assignment.moduleName = lesson?.moduleName
                assignment.lesson = lesson
                assignment.type = lesson?.type as Type
                assignment.lessonId = lesson?.id
                assignment.assessmentId = postTestAssessment?.id
                assignment.assessmentNumber = knowledgeCheckAssessmentId ? 1 : lesson?.assessment?.number
            }
            navigate("/dashboard/student/assignments/assignment", {
                state: {
                    submissionId: await getStudentSubmissionId({ assignment: assignment, recentSubmission: null }),
                    assignment: assignment,
                    continueAssessment: false,
                    reworkAssignmentMode: false,
                    isReview: true,
                } as StudentAssignmentStateProps
            })
        } catch (err) {
            toast.error(err?.response?.data?.message ?? "Unable to start assignment, please try again.")
        }
    }

    if (!reviewAssignment) {
        return (
            <div className="d-flex flex-column text-start">
                <p className="fw-bold fs-3">Explore Core Ideas</p>
                <p>Core ideas are a way to explore some aspect of science. By testing what you know, then investigating the lessons, you can build your understanding of the core ideas.</p>
                <p className="fw-bold fs-5">Select one to get started.</p>
            </div>
        )
    }

    const { name, standardSubtopic, pretestGrade, lessonGrade, assignment, lessonViewed, knowledgeCheckAssessmentId } = reviewAssignment
    const pretestStatus = gradeStatus(pretestGrade) === "Success" ? stepSuccess : (gradeStatus(pretestGrade) === "Failure" ? stepFailure : stepNotStarted)
    const lessonStatus = gradeStatus(lessonGrade) === "Success" ? stepSuccess : (gradeStatus(lessonGrade) === "Failure" ? stepFailure : stepNotStarted)

    const knowledgeCheckPending = pretestGrade === null
    const sciencePending = !knowledgeCheckPending && !lessonViewed
    const recheckPending = !knowledgeCheckPending && lessonViewed

    return (
        <div className={"d-flex flex-column align-items-start text-start"} style={{ width: "24rem" }}>
            <p className="fw-bold text-muted fs-4 m-0">Core Idea:</p>
            <p className="fw-bold fs-3" dangerouslySetInnerHTML={{ __html: name}}></p>
            <p>
                {standardSubtopic}
            </p>
            <div className="position-relative">
                {/* Knowledge Check */}
                <div className={`d-flex align-items-center flex-nowrap px-3 py-2 ${knowledgeCheckPending ? styles.activeStepRow : ""}`} style={{ gap: "8px" }}>
                    <div className={`${styles.gradeArrowSlot}`}>
                        {knowledgeCheckPending ?
                            <i className={`${styles.startArrow} fa-solid fa-arrow-right-long fw-bold fs-5 text-primary`} /> :
                            <p className="fw-bold fs-5 m-0">{pretestGrade.toFixed(0)}%</p>
                        }
                    </div>
                    <div className={`circle ${pretestStatus}`}></div>
                    <div className="text-truncate">
                        {knowledgeCheckPending ?
                            <button className="m-0 fw-bold fs-5 btn btn-link text-decoration-none p-0 text-truncate" onClick={() => openAssignment(assignment, "pretest", knowledgeCheckAssessmentId)}>Knowledge&nbsp;Check</button> :
                            <p className="m-0 fw-bold fs-5 text-truncate">Knowledge&nbsp;Check</p>
                        }
                    </div>
                </div>

                <div className="d-flex align-items-center" style={{ gap: "22px" }}>
                    <div className={`${styles.gradeArrowSlot}`} />
                    <div className="d-flex flex-column text-primary" style={{ width: "20px", flexShrink: 0, opacity: knowledgeCheckPending ? 0 : 0.75 }}>
                        <i className={`${sciencePending ? styles.animatedCaret : styles.caret} ${styles.caret1} fw-bold fs-3 fa-solid fa-caret-down mx-auto`}></i>
                        <i className={`${sciencePending ? styles.animatedCaret : styles.caret} ${styles.caret2} fw-bold fs-4 fa-solid fa-caret-down mx-auto`} style={{ marginTop: "-0.35em" }}></i>
                        <i className={`${sciencePending ? styles.animatedCaret : styles.caret} ${styles.caret3} fw-bold fs-5 fa-solid fa-caret-down mx-auto`} style={{ marginTop: "-0.25em" }}></i>
                    </div>
                </div>

                {/* Science */}
                <div className={`d-flex align-items-center flex-nowrap px-3 py-2 ${sciencePending ? styles.activeStepRow : ""}`} style={{ gap: "8px" }}>
                    <div className={`${styles.gradeArrowSlot}`}>
                        {sciencePending &&
                            <i className={`${styles.startArrow} fa-solid fa-arrow-right-long fw-bold fs-5 text-primary`} />
                        }
                    </div>
                    <div className={`circle ${lessonStatus}`}></div>
                    <div className="text-truncate">
                        {sciencePending ?
                            <button className="m-0 fw-bold fs-5 btn btn-link text-decoration-none p-0 text-truncate" onClick={() => openAssignment(assignment, "lesson", knowledgeCheckAssessmentId)}>Science</button> :
                            <p className="m-0 fw-bold fs-5 text-truncate">Science</p>
                        }
                    </div>
                </div>

                <div className="d-flex align-items-center" style={{ gap: "22px" }}>
                    <div className={`${styles.gradeArrowSlot}`} />
                    <div
                        className="d-flex flex-column text-primary"
                        style={{ width: "20px", flexShrink: 0, opacity: recheckPending ? 0.75 : 0 }}
                    >
                        <i className={`${styles.animatedCaret} ${styles.caret1} fw-bold fs-3 fa-solid fa-caret-down mx-auto`}></i>
                        <i className={`${styles.animatedCaret} ${styles.caret2} fw-bold fs-4 fa-solid fa-caret-down mx-auto`} style={{ marginTop: "-0.35em" }}></i>
                        <i className={`${styles.animatedCaret} ${styles.caret3} fw-bold fs-5 fa-solid fa-caret-down mx-auto`} style={{ marginTop: "-0.25em" }}></i>
                    </div>
                </div>

                {/* Recheck */}
                <div className={`d-flex align-items-center flex-nowrap px-3 py-2 ${recheckPending ? styles.activeStepRow : ""}`} style={{ gap: "8px" }}>
                    <div className={`${styles.gradeArrowSlot}`}>
                        {recheckPending && lessonGrade !== null &&
                            <p className="fw-bold fs-5 m-0">{lessonGrade.toFixed(0)}%</p>
                        }
                        {recheckPending && lessonGrade === null &&
                           <i className={`${styles.startArrow} fa-solid fa-arrow-right-long fw-bold fs-5 text-primary`} />
                        }
                    </div>
                    <div className={`circle ${lessonStatus}`}></div>
                    <div className="text-truncate">
                        {recheckPending ?
                            <button className="m-0 fw-bold fs-5 btn btn-link text-decoration-none p-0 text-truncate" onClick={() => openAssignment(assignment, "lesson", knowledgeCheckAssessmentId)}>Recheck</button> :
                            <p className="m-0 fw-bold fs-5 text-truncate">Recheck</p>
                        }
                    </div>
                </div>
            </div>
            <p className="fw-bold fs-5 mt-3">What's Next?</p>
            <p className="fs-6">
                {isMastered(reviewAssignment) ?
                    "You've completed the core idea. You can redo the Science lesson to learn more about this core idea or move on to the next core idea."
                : knowledgeCheckPending ?
                    "Click on the Knowledge Check to get started on this core idea!"
                : sciencePending ?
                    "Click on Science to learn more about this core idea."
                :
                    "Complete the Review to see how much you've learned about this core idea. You need to score at least 75% to have mastered it."}
            </p>
        </div>
    )
}

export default StudentCoreIdeaDetails
